import { ItemDetailMatchSummary } from "@/model/entities/-item-detail-match-summary";
import { OrderAnalysis, OrderAnalysisListResult } from "@/model/entities/-order-analysis";
import { CloseStrandedResult, StrandedOrderResult } from "@/model/entities/-stranded-order";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  AccountFulfillmentTime,
  AddonStationProcessedKpi,
  AccountOverdueOrder,
  RedZoneJoDetail,
  InvoiceDelayDataForChart,
  JobOrdersByStatusForChart,
  JoInProcessOpenValue,
  ManufacturerCountInInventory,
  ManufacturerReceiptTime,
  OnTimeShippedKpi,
  OrdersShippedCompleteKpi,
  ProductTypeCountInInventory,
  ShippedInvoiceRevenue,
} from "app/model/entities/-chart-classes";
import { ItemDetailSummary } from "app/model/entities/-item-detail-summary";
import { ProductNeededForEOQ } from "app/model/entities/-product-type-summary";
import {
  Account,
  ActiveStatus,
  Addon,
  AddonBin,
  AddonBinStationStatus,
  AddonBinStatus,
  AddonBinStatusEnum,
  AddonStation,
  BarcodePrintStatus,
  BoxSize,
  Config,
  CreditMemo,
  DocSubmitType,
  ManufacturerFeedStatus,
  ManufacturerFeedStatusEnum,
  EntityIssue,
  EOQType,
  Feature,
  FeatureChoice,
  Image,
  InvoiceDetail,
  InvoiceHeader,
  InvoiceStatus,
  ItemBin,
  ItemDetail,
  ItemDetailAddon,
  ItemDetailLog,
  ItemDetailStatus,
  ItemDetailStatusEnum,
  ItemReplacementStatus,
  ItemReplacementType,
  JoDetail,
  JoDetailAddon,
  JoHeader,
  JoNote,
  JoPullBatch,
  JoStatus,
  Manifest,
  ManifestGroup,
  ManifestGroupInvoice,
  Manufacturer,
  ManufacturerProductTypeConfig,
  NotificationTemplate,
  PoDetail,
  PoDetailType,
  PoHeader,
  PoNote,
  PoNoteType,
  PoStatus,
  PoTrx,
  PoTrxHist,
  Product,
  ProductEOQ,
  ProductFeatureChoice,
  ProductType,
  ProductTypeAlt,
  ProductTypeTag,
  ProductTypeTagMap,
  ProxSubmission,
  ProxSubmissionStatus,
  QBAccount,
  QBItem,
  ReturnReasonType,
  ReturnRequest,
  ReturnRequestDetail,
  ReturnRequestDetailStatus,
  ReturnRequestStatus,
  ShipBin,
  ShipFrequency,
  Shipment,
  SystemImage,
  WarningType,
  FeatureKind,
  ManufacturerFeatureKindMap,
  ProductReplacement,
} from "app/model/entities/entity-model";
import { ReplacementMap } from "./pull-match-fns";
import { FeatureKindEnum } from "app/model/entities/-feature-kind-enum";
import { InvoiceStatusCode } from "app/model/enums/invoice-status-code";
import { JobStatusCodeExt, JoStatusCode } from "app/model/enums/jo-status-code";
import { PoStatusCode } from "app/model/enums/po-status-code";
import { Entity, FilterQueryOp, Predicate } from "breeze-client";
import { environment } from "environments/environment";
import _ from "lodash";
import { RRule } from "rrule";
import { firstValueFrom } from "rxjs";
import { DateFns } from "./date-fns";
import { UnitOfWork } from "./unit-of-work";

interface HasRowVersion {
  rowVersion: any;
}

interface AppConfig {
  productTypeDefault?: {
    orderingCostAmt: number;
    unitCarryingCostPerYearAmt: number;
    safetyStockPerYearPct: number;
  };
  unitecAddress?: {
    city: string;
    line1: string;
    name: string;
    phone: string;
    zipCode: string;
    state: string;
  };
  account?: {
    contactSchedule: string;
    dueDateNumDays: number;
  };
  addon?: {
    maxBatchSize: number;
  };
  freight?: {
    markupPct: number;
  };
}

export class FieldLike {
  constructor(public value: string, public fieldOp: FilterQueryOp = FilterQueryOp.StartsWith) {}

  static create(value: string, fieldOp?: FilterQueryOp) {
    if (value && value.trim().length > 0) {
      return new FieldLike(value, fieldOp);
    } else {
      return undefined;
    }
  }

  toClause() {
    const clause = {};
    clause[this.fieldOp.name] = this.value;
    return clause;
  }
}

export type ProductsWhere = {
  manufacturerName?: FieldLike;
  productTypeDescription?: FieldLike;
  productTypeDescriptionExt?: FieldLike;
  productTypeStyle?: FieldLike;
  features?: Feature[];
  featureChoices?: FeatureChoice[];
  featuresAndOr?: "and" | "or";
  productTypeTags?: ProductTypeTag[];
  productTypeTagsAndOr?: "and" | "or";
};

export type FeatureSearchCriteria = {
  feature: Feature;
  values: string[];
};

@Injectable({ providedIn: "root" })
export class DbQueryService {
  cachedLookups = {};
  private cacheAllPromise: Promise<any>;
  public appConfig: AppConfig;

  constructor(public uow: UnitOfWork, private http: HttpClient) {}

  getUnitecAddressAsString() {
    const addr = this.appConfig.unitecAddress;
    return `${addr.name}\n${addr.line1}\n${addr.city}, ${addr.state} ${addr.zipCode}\n`;
  }

  async cacheAll(shouldIncludeConfigs = true) {
    if (this.cacheAllPromise) {
      return this.cacheAllPromise;
    }

    // cache a bunch of lookups

    this.cacheAllPromise = this.uow.manager.executeQuery("lookups");
    await this.cacheAllPromise;
    this.cache(ActiveStatus, "id", "name");
    this.cache(AddonBinStationStatus, "id", "name");
    this.cache(AddonBinStatus, "id", "name");
    this.cache(JoStatus, "id", "name");
    this.cache(InvoiceStatus, "id", "name");
    this.cache(ItemDetailStatus, "id", "name");
    this.cache(PoStatus, "id", "name");
    this.cache(PoDetailType, "id", "name");
    this.cache(PoNoteType, "id", "name");
    this.cache(ProxSubmissionStatus, "id", "name");
    this.cache(DocSubmitType, "id", "name");
    this.cache(ManufacturerFeedStatus, "id", "name");
    this.cache(FeatureKind, "id", "name");
    this.cache(BarcodePrintStatus, "id", "name");
    this.cache(ItemReplacementStatus, "id", "name");
    this.cache(ItemReplacementType, "id", "name");
    this.cache(ReturnRequestStatus, "id", "name");
    this.cache(ReturnReasonType, "id", "name");
    this.cache(ReturnRequestDetailStatus, "id", "name");
    this.cache(WarningType, "id", "name");
    this.cache(EOQType, "id", "name");
    this.cache(ShipFrequency, "id", "name");
    this.cache(BoxSize, "id", "name");

    if (shouldIncludeConfigs) {
      const configs = await this.getConfigs();
      this.updateAppConfig(configs);
    }
  }

  updateAppConfig(configs: Config[]) {
    this.appConfig = this.parseConfigs(configs);
    InvoiceHeader.freightMarkupRatio = (this.appConfig as any).Freight?.MarkupPct ?? 0;
  }

  private parseConfigs(configs: Config[]) {
    let appConfig: AppConfig = {};
    configs.forEach((c) => {
      if (appConfig[c.groupName] == null) {
        appConfig[c.groupName] = {};
      }
      if (c.fieldType == "string") {
        appConfig[c.groupName][c.fieldName] = c.value;
      } else if (c.fieldType == "number") {
        appConfig[c.groupName][c.fieldName] = Number.parseFloat(c.value);
      }
    });
    return appConfig;
  }

  createQuery<T extends Entity>(type: new () => T, resourceName: string = null) {
    return this.uow.createQuery(type, resourceName);
  }

  async getAll<T extends Entity>(type: new () => T, skipCache = false) {
    const r = this.uow.createQuery(type).execute(skipCache);
    return r;
  }

  // used to get all of a type that has already been fetched into the entityManager cache
  // intended for different use case than getAllCached which is
  getAlreadyFetched<T extends Entity>(type: new () => T) {
    const r = this.uow.getEntities(type);
    return r;
  }

  getAllCached<T extends Entity>(type: new () => T) {
    const r = this.uow.getEntities(type);
    if (r.length === 0) {
      throw Error("Not a cached resource: " + type.name);
    }
    return r;
  }

  async cacheAndGetAll<T extends Entity>(type: new () => T) {
    await this.cacheAll();
    return this.getAllCached(type);
  }

  private cache<T extends Entity>(type: new () => T, keyProp: string, valueProp: string) {
    const typeName = type.prototype.entityType.shortName;
    let map = this.cachedLookups[typeName];
    if (map) {
      return map;
    }
    const r = this.uow.getEntities(type);
    map = _.chain(r).keyBy(keyProp).mapValues(valueProp).value();
    this.cachedLookups[typeName] = map;
    return map;
  }

  lookup(typeName: string, id: any) {
    const map = this.cachedLookups[typeName];
    if (map == null) {
      return null;
    }
    return map[id];
  }

  async getById<T extends Entity>(type: new () => T, id: any) {
    return this.uow.fetchEntityByKey(type, id);
  }

  async checkRowVersion(entity: Entity & HasRowVersion) {
    const rowVersion = entity.rowVersion;
    const r = await entity.entityAspect.entityManager.fetchEntityByKey(entity.entityAspect.getKey());
    // r.entity will be null if the entity no longer exists.
    if (r.entity == null || entity.rowVersion != rowVersion) {
      throw "optimistic concurrency error";
    }
  }

  // fieldKey is the real key field of entity, fieldsNames are list of fields that are the alternate keys
  async checkIfIsUnique<T extends Entity>(entity: T, fieldKey: string, ...fieldNames: string[]): Promise<boolean> {
    const type = entity.constructor as { new (): T };
    const where = {};
    fieldNames.forEach((f) => (where[f] = entity[f]));
    const r = await this.uow.createQuery(type).where(where).execute();

    return r.length == 0 ? true : r.length == 1 && r[0][fieldKey] === entity[fieldKey];
  }

  async checkIfInUse<T1 extends Entity, T2 extends Entity, K extends keyof T2>(
    entity: T1,
    dependentType: new () => T2,
    depFieldName: K
  ): Promise<boolean> {
    const where = {};
    where[depFieldName as string] = entity["id"];
    const r = await this.uow.createQuery(dependentType).where(where).take(1).execute();

    return r.length > 0;
  }

  // async checkIfInUse<T1 extends Entity, T2 extends Entity>(entity: T1, dependentType: new () => T2, depFieldName: string): Promise<boolean> {
  //   const where = {};
  //   where[depFieldName] = entity['id'];
  //   const r = await this.uow.createQuery(dependentType)
  //     .where(where)
  //     .execute();

  //   return r.length > 0;
  // }

  // Specific queries

  createProductsWhereQuery(whereObj: ProductsWhere) {
    const whereClause = {};

    whereClause["productType.manufacturer.name"] = whereObj.manufacturerName?.toClause();
    whereClause["productType.description"] = whereObj.productTypeDescription?.toClause();
    whereClause["productType.descriptionExt"] = whereObj.productTypeDescriptionExt?.toClause();

    whereClause["productType.style"] = whereObj.productTypeStyle?.toClause();

    // remove empty clauses
    Object.keys(whereClause).forEach((k) => {
      if (whereClause[k] == null || whereClause[k].length == 0) {
        delete whereClause[k];
      }
    });

    let query = this.uow.createQuery<Product>(Product, "ProductsWhere").where(whereClause).expand(["productType.manufacturer"]);

    let parameters = {};
    if (whereObj.productTypeTags) {
      parameters = {
        tagNames: whereObj.productTypeTags.map((pt) => pt.name),
        tagNamesAndOr: whereObj.productTypeTagsAndOr ?? "and",
      };
    }

    let whereFeatures = whereObj.features;
    if (whereObj.featureChoices && whereFeatures) {
      const featureSet = new Set(whereObj.featureChoices?.map((x) => x.feature));
      // reduce whereFeatures by features in featureChoices
      whereFeatures = whereFeatures.filter((f) => !featureSet.has(f));
    }

    parameters = {
      ...parameters,
      featureIds: whereFeatures?.map((x) => x.id),
      featureChoiceIds: whereObj.featureChoices?.map((x) => x.id),
      featuresAndOr: whereObj.featuresAndOr ?? "and",
    };

    query = query.withParameters(parameters);
    return query;
  }

  async getProductsWhere(whereObj: ProductsWhere) {
    const query = this.createProductsWhereQuery(whereObj);
    const r = await query.execute();

    return r;
  }

  async getAllFeatureRelatedInfo() {
    const p1 = this.uow
      .createQuery(Feature)

      .expand(["featureChoices", "productTypeFeatureMaps"])
      .execute();
    const p2 = this.uow
      .createQuery(ProductTypeTag)

      .execute();
    const r = await Promise.all([p1, p2]);

    return r;
  }

  createJoHeaderQuery() {
    const q = this.uow
      .createQuery(JoHeader, "JoHeaders")
      // .where({ not: { joStatusId: JoStatusCode.Closed }})
      .where({
        joStatusId: {
          in: [JoStatusCode.Closed, JoStatusCode.OpenProcessComplete],
        },
      })
      .expand([
        "account",
        "joDetails",
        "joDetails.cancellationRequestDetails",
        "joDetails.returnRequestDetails",
      ]);
    return q;
  }

  createJoHeaderQueryForStatusAndMonthOf(joStatusId: string, date: Date) {
    const dtStart = DateFns.startOfMonth(date);
    const dtEnd = DateFns.endOfMonth(date);
    const q = this.uow
      .createQuery(JoHeader, "JoHeaders")

      .where({
        joStatusId: joStatusId,
        joDate: { ">=": dtStart, "<": dtEnd },
      })
      .expand([
        "joDetails",
        "joDetails.cancellationRequestDetails",
        "joDetails.returnRequestDetails",
      ]);
    // const r = await q.execute();
    // return r;
    return q;
  }

  createJoHeadersWithPostedInvoicesQuery() {
    const q = this.uow.createQuery(JoHeader, "GetJoHeadersWithPostedInvoices").expand([
      "account",
      "joDetails",
      "joDetails.cancellationRequestDetails",
      "joDetails.returnRequestDetails",
    ]);
    return q;
  }

  createJoHeaderModeQuery(mode: JobStatusCodeExt) {
    const q = this.uow.createQuery(JoHeader, "GetJoHeadersForJobOrderMode")
      .withParameters({ jobOrderMode: mode })
      .expand(["joDetails.cancellationRequestDetails", "joDetails.returnRequestDetails"]);
    return q;
  }

  createJoHeadersInProcessQuery() {
    const q = this.uow.createQuery(JoHeader, "GetJoHeadersInProcess").expand(["account"]);
    return q;
  }

  createOpenJoHeadersQuery() {
    const q = this.uow.createQuery(JoHeader, "GetOpenJoHeaders").expand(["account"]);
    return q;
  }

  createJoHeadersInShippingProcessQuery() {
    const q = this.uow.createQuery(JoHeader, "GetJoHeadersInShippingProcess").expand(["account"]);
    return q;
  }

  async getOpenJoDetailReport(accountId?: number, shippingAddress1?: string) {
    let q = this.uow.createQuery(JoDetail, "GetOpenJoDetailReport");
    const params: Record<string, unknown> = {};
    if (accountId != null) params.accountId = accountId;
    if (shippingAddress1 != null) params.shippingAddress1 = shippingAddress1;
    if (Object.keys(params).length > 0) {
      q = q.withParameters(params);
    }
    const r = await q.execute();
    return r as JoDetail[];
  }

  createInvoiceQuery(shouldIncludePosted = false) {
    let q = this.uow
      .createQuery(InvoiceHeader, "InvoiceHeaders")
      .expand([
        "invoiceDetails.joDetail.product.productType.manufacturer",
        "invoiceDetails.joDetail.product.productFeatureChoices.featureChoice",
        "accountPurchaseItemDetails",
        "joHeader.account",
        "shipment",
      ]);
    if (!shouldIncludePosted) {
      q = q.where({ invoiceStatusId: { ne: InvoiceStatusCode.InvoicePosted } });
    }
    return q;
  }

  async getAccountOwnedInvoices(filter: "actionable" | "posted") {
    let q = this.uow
      .createQuery(InvoiceHeader, "InvoiceHeaders")
      .where({ "joHeader.isAccountOwnedOrder": true })
      .expand([
        "invoiceDetails.joDetail.product.productType.manufacturer",
        "invoiceDetails.joDetail.product.productFeatureChoices.featureChoice",
        "accountPurchaseItemDetails",
        "joHeader.account",
        "invoiceStatus",
      ]);
    if (filter === "actionable") {
      q = q.where({
        invoiceStatusId: {
          in: [InvoiceStatusCode.Voucher, InvoiceStatusCode.Printed, InvoiceStatusCode.AwaitPost],
        },
      });
    } else {
      q = q.where({ invoiceStatusId: InvoiceStatusCode.InvoicePosted });
    }
    return await q.execute();
  }

  createReturnRequestQuery() {
    const q = this.uow
      .createQuery(ReturnRequest, "ReturnRequests")
      .expand(["returnRequestDetails.creditMemos", "joHeader.account", "joHeader.invoiceHeaders"]);
    return q;
  }

  createUnsentManifestGroupQuery() {
    const q = this.uow
      .createQuery(ManifestGroup, "ManifestGroups")
      .where({ sentDate: null })
      .expand([
        "manifest.joHeaders.account",
        "manifestGroupInvoices.invoiceHeader.joHeader.account",
        "manifestGroupInvoices.invoiceHeader.invoiceStatus",
      ]);
    return q;
  }

  createManifestGroupQuery() {
    const q = this.uow
      .createQuery(ManifestGroup, "ManifestGroups")
      .where({ sentDate: { ne: null } })
      .expand(["manifest.joHeaders.account", "manifestGroupInvoices"]);
    return q;
  }

  createPoHeaderQuery(poStatusId: string) {
    let q = this.uow.createQuery(PoHeader, "PoHeaders");
    if (poStatusId != null) {
      q = q.where({ poStatusId: poStatusId });
    }
    q = q.expand(["manufacturer", "poNotes"]);
    return q;
  }

  createCompletedPoHeadersWithOpenQtyQuery() {
    return this.uow
      .createQuery(PoHeader, "CompletedPoHeadersWithOpenQty")
      .expand(["manufacturer", "poNotes"]);
  }

  createItemDetailAddonQuery() {
    const whereClause = {
      "itemDetail.itemDetailStatusId": {
        in: [ItemDetailStatusEnum.InProcessPendingAddon, ItemDetailStatusEnum.InProcessAddon],
      },
    };
    const q = this.uow
      .createQuery(ItemDetailAddon, "ItemDetailAddons")
      .where(whereClause)
      .expand([
        "itemDetail",
        "itemDetail.product.productType.manufacturer",
        "itemDetail.product.productFeatureChoices.featureChoice",
        "addon.addonDocMaps.addonDoc",
        "itemDetail.joDetail",
        "itemDetail.poDetail",
      ]);

    return q;
  }

  createItemDetailSummaryQuery(isAccountOwned: boolean) {
    let q = this.uow.createQuery(null, "GetItemDetailSummaries"); // method on server
    if (isAccountOwned !== null) {
      q = q.withParameters({ isAccountOwned: isAccountOwned });
    }
    return q;
  }

  createItemDetailLogQuery(itemDetailId: string) {
    const q = this.uow
      .createQuery(ItemDetailLog)
      .where({ itemDetailId })
      .expand(["itemDetail.product.productType", "joDetail.joHeader.account", "poDetail", "itemBin", "account"]);

    return q;
  }

  async getPoDetailBackorders() {
    // const q = this.uow.createQuery(null, 'GetPoDetailBackorders'); // method on server
    const q = this.uow.createQuery(null, "GetPoDetailBackorders"); // method on server
    const r = await q.execute();
    return r;
  }

  async getPoDetailAccountBackorders(accountGuid) {
    // method on server
    const q = this.uow.createQuery(null, "GetPoDetailAccountBackorders").withParameters({ guid: accountGuid });
    const r = await q.execute();
    return r;
  }

  async getPoDetailManufacturerBackorders(manufacturerGuid) {
    // method on server
    const q = this.uow.createQuery(null, "GetPoDetailManufacturerBackorders").withParameters({ guid: manufacturerGuid });
    const r = await q.execute();
    return r;
  }

  createProductTypeAccountSummaryQuery(productTypeId: number) {
    // method on server
    const q = this.uow.createQuery(null, "GetProductTypeAccountSummaries").withParameters({ productTypeId: productTypeId });
    return q;
  }

  createProductSummaryQuery(productTypeId: number) {
    // method on server
    const q = this.uow.createQuery(null, "GetProductSummaries").withParameters({ productTypeId: productTypeId });
    return q;
  }

  createAccountQuery(shouldShowReissueAccountsOnly: boolean = false) {
    let q = this.uow.createQuery(Account, "Accounts");
    if (shouldShowReissueAccountsOnly) {
      q = q.where({
        allowReissuanceOrders: true,
      });
    }

    return q;
  }

  createManufacturerQuery() {
    let q = this.uow.createQuery(Manufacturer).expand(["manufacturerCryptonym", "manufacturerImages"]);

    return q;
  }

  createManufacturerProductTypeConfigQuery(manufacturerId?: number) {
    let q = this.uow.createQuery(ManufacturerProductTypeConfig, "ManufacturerProductTypeConfigs");
    if (manufacturerId) {
      q = q.where({ "productType.manufacturerId": manufacturerId });
    }
    q = q.expand(["productType.manufacturer", "manufacturerProductTypeConfigAddons.addon.addonStation"]);

    return q;
  }

  createProductQuery() {
    let q = this.uow.createQuery(Product);
    q = q.expand(["productType.manufacturer", "productFeatureChoices.featureChoice"]);
    return q;
  }

  /** Fetch a single Product with manufacturer + style + feature choices loaded
   * — same expansions the standard product-display helpers expect. */
  async getProductById(productId: number): Promise<Product | null> {
    if (productId == null) return null;
    const q = this.uow
      .createQuery(Product, "Products")
      .where({ id: productId })
      .expand(["productType.manufacturer", "productFeatureChoices.featureChoice.feature"]);
    const r = await q.execute();
    return (r?.[0] as Product) ?? null;
  }

  createProductTypeFinderQuery(manufacturerId?: number) {
    let q = this.uow.createQuery(ProductType);
    if (manufacturerId) {
      q = q.where({ manufacturerId: manufacturerId });
    }
    q = q.expand(["manufacturer"]);
    return q;
  }

  createAddonFinderQuery(excludeId: number = null) {
    let q = this.uow.createQuery(Addon);
    q = q.expand(["account", "addonStation"]);
    return q;
  }

  createProductTypesQuery(excludeId: number = null) {
    let q = this.uow.createQuery(ProductType, "ProductTypes");
    q = q.expand([
      "manufacturer",
      "productTypeAltProductTypes",
      "productTypeFeatureMaps.feature",
      "products",
      "products.productFeatureChoices.featureChoice",
    ]);
    if (excludeId) {
      q = q.where({ not: { id: excludeId } });
    }
    return q;
  }

  createProductTypesForFeatureQuery(featureId: number) {
    let q = this.uow
      .createQuery(ProductType)
      .where({
        productTypeFeatureMaps: { any: { featureId: featureId } },
      })
      .expand(["manufacturer", "productTypeFeatureMaps.feature"]);
    return q;
  }

  createProductTypesForEOQQuery(excludeId: number = null) {
    let q = this.uow.createQuery(ProductType, "ProductTypes");
    q = q.expand(["manufacturer", "products"]);
    if (excludeId) {
      q = q.where({ not: { id: excludeId } });
    }
    // q = q.orderBy('manufacturer.name');
    // q = q.orderBy('style');
    return q;
  }

  async getProductTypesForImageMatching(): Promise<ProductType[]> {
    const q = this.uow
      .createQuery(ProductType, "ProductTypes")
      .expand(["manufacturer", "productTypeImages"]);
    return await q.execute();
  }

  async getConfigs(groupName?: string, fieldName?: string) {
    let q = this.uow.createQuery(Config, "Configs");
    if (groupName !== null) {
      q = q.where({ groupName: groupName });
    }
    if (fieldName !== null) {
      q = q.where({ fieldName: fieldName });
    }
    const r = await q.execute();
    return r;
  }

  async getQBAccounts() {
    let q = this.uow.createQuery(QBAccount);
    const r = await q.execute();
    return r;
  }

  async getQBItems() {
    let q = this.uow.createQuery(QBItem);
    const r = await q.execute();
    return r;
  }

  async getItemBins() {
    let q = this.uow.createQuery(ItemBin, "ItemBins");
    const r = await q.execute();
    return r;
  }

  async getProductsNeededForEOQ() {
    const q = this.uow.createQuery(null, "GetProductsNeededForEOQ");
    const r = (await q.execute()) as unknown as ProductNeededForEOQ[];
    return r;
  }

  /** Accounts set to Exclude from Dashboard Analytics on Edit Account. */
  async getAnalyticsExcludedAccounts() {
    const q = this.uow.createQuery(Account, "Accounts").where({ excludeFromAnalytics: true });
    const r = await q.execute();
    return _.sortBy(r, (a) => a.accountName);
  }

  async getAccountsWithInvoices() {
    const q = this.uow.createQuery(Account, "GetAccountsWithInvoices");
    const r = await q.execute();
    return r;
  }

  /** includeAccountIds: accounts set to Exclude from Dashboard Analytics that this view puts back in. */
  async getInvoiceDelayDataForChart(accountId?: number, includeAccountIds: number[] = []) {
    let q = this.uow.createQuery(null, "GetInvoiceDelayDataForChart");
    const params: Record<string, unknown> = { includeAccountIds: includeAccountIds.join(",") };
    if (accountId != null) params.accountId = accountId;
    q = q.withParameters(params);
    const r = (await q.execute()) as unknown as InvoiceDelayDataForChart[];
    return r;
  }

  async getTopManufacturersInInventory() {
    let q = this.uow.createQuery(null, "GetTopManufacturersInInventory");

    const r = (await q.execute()) as unknown as ManufacturerCountInInventory[];
    return r;
  }

  async getTopProductTypesInInventoryForManufacturer(manufacturerId: number) {
    let q = this.uow.createQuery(null, "GetTopProductTypesInInventoryForManufacturer").withParameters({ manufacturerId });

    const r = (await q.execute()) as unknown as ProductTypeCountInInventory[];
    return r;
  }

  async getAccountFulfillmentTimes(monthsBack = 6, includeAccountIds: number[] = []) {
    const q = this.uow
      .createQuery(null, "GetAccountFulfillmentTimes")
      .withParameters({ monthsBack, includeAccountIds: includeAccountIds.join(",") });
    const r = (await q.execute()) as unknown as AccountFulfillmentTime[];
    return r;
  }

  async getManufacturerReceiptTimes(monthsBack = 6, includeAccountIds: number[] = []) {
    const q = this.uow
      .createQuery(null, "GetManufacturerReceiptTimes")
      .withParameters({ monthsBack, includeAccountIds: includeAccountIds.join(",") });
    const r = (await q.execute()) as unknown as ManufacturerReceiptTime[];
    return r;
  }

  async getAccountOverdueOrders(includeAccountIds: number[] = []) {
    const q = this.uow
      .createQuery(null, "GetAccountOverdueOrders")
      .withParameters({ includeAccountIds: includeAccountIds.join(",") });
    const r = (await q.execute()) as unknown as AccountOverdueOrder[];
    return r;
  }

  async getJoInProcessOpenValues() {
    const q = this.uow.createQuery(null, "GetJoInProcessOpenValues");
    const r = (await q.execute()) as unknown as JoInProcessOpenValue[];
    return r;
  }

  /** accountId 0 (or none) returns every account except those excluded from analytics and not in includeAccountIds. */
  async getRedZoneJoDetails(accountId = 0, includeAccountIds: number[] = []) {
    const q = this.uow
      .createQuery(null, "GetRedZoneJoDetails")
      .withParameters({ accountId, includeAccountIds: includeAccountIds.join(",") });
    const r = (await q.execute()) as unknown as RedZoneJoDetail[];
    return r;
  }

  /** Dates are inclusive yyyy-MM-dd. Every account except those excluded from analytics and not in includeAccountIds. */
  async getOnTimeShippedKpi(fromDate: string, toDate: string, includeAccountIds: number[] = []) {
    const q = this.uow
      .createQuery(null, "GetOnTimeShippedKpi")
      .withParameters({ fromDate, toDate, includeAccountIds: includeAccountIds.join(",") });
    const r = (await q.execute()) as unknown as OnTimeShippedKpi[];
    return _.first(r) ?? { totalUnitCount: 0, onTimeUnitCount: 0, totalValueAmt: 0, onTimeValueAmt: 0 };
  }

  /** Dates are inclusive yyyy-MM-dd. Every account except those excluded from analytics and not in includeAccountIds. */
  async getOrdersShippedCompleteKpi(fromDate: string, toDate: string, includeAccountIds: number[] = []) {
    const q = this.uow
      .createQuery(null, "GetOrdersShippedCompleteKpi")
      .withParameters({ fromDate, toDate, includeAccountIds: includeAccountIds.join(",") });
    const r = (await q.execute()) as unknown as OrdersShippedCompleteKpi[];
    return _.first(r) ?? { firstShippedOrderCount: 0, completeOrderCount: 0 };
  }

  /** Dates are inclusive yyyy-MM-dd. One row per Process Station. Every account except those excluded from analytics and not in includeAccountIds. */
  async getAddonStationProcessedKpi(fromDate: string, toDate: string, includeAccountIds: number[] = []) {
    const q = this.uow
      .createQuery(null, "GetAddonStationProcessedKpi")
      .withParameters({ fromDate, toDate, includeAccountIds: includeAccountIds.join(",") });
    return (await q.execute()) as unknown as AddonStationProcessedKpi[];
  }

  async getShippedInvoiceRevenues(weeksBack = 12) {
    const q = this.uow.createQuery(null, "GetShippedInvoiceRevenues").withParameters({ weeksBack });
    const r = (await q.execute()) as unknown as ShippedInvoiceRevenue[];
    return r;
  }

  async getJobOrdersByStatusForChart(includeAccountIds: number[] = []) {
    const q = this.uow
      .createQuery(null, "GetJobOrdersByStatusForChart")
      .withParameters({ includeAccountIds: includeAccountIds.join(",") });

    const r = (await q.execute()) as unknown as JobOrdersByStatusForChart[];
    return r;
  }

  async getItemCountInInventory() {
    let q = this.uow.createQuery(null, "GetItemCountInInventory");
    const r = await q.execute();
    return _.first(r) as any as number;
  }

  async getManifestLastSentDate(manifestId: number) {
    const q = this.uow
      .createQuery(ManifestGroupInvoice, "ManifestGroupInvoices")
      .where({ "manifestGroup.manifest.id": manifestId, "manifestGroup.sentDate": { ne: null } })
      .orderBy("manifestGroup.sentDate", true)
      .take(1)
      // Expanded, not assumed in the cache: an older sent group is not on the Manifest Groups page,
      // so its navigation came back null and reading sentDate threw.
      .expand("manifestGroup");
    const r = await q.execute();
    return r.length > 0 ? r[0].manifestGroup?.sentDate ?? null : null;
  }

  async getManifestNextProcessDate(manifest: Manifest) {
    const ruleString = manifest.rRule;
    if (ruleString == null) {
      return null;
    }

    const ruleObj = RRule.fromString(ruleString);
    let lastDt = await this.getManifestLastSentDate(manifest.id);
    const now = new Date();
    const today = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    let nextDt: Date;
    if (lastDt == null) {
      nextDt = ruleObj.after(today, true);
    } else {
      lastDt = new Date(lastDt.getFullYear(), lastDt.getMonth(), lastDt.getDate());
      nextDt = ruleObj.after(lastDt, false);
    }
    // Never a date already behind us. A manifest with nothing to send skips its runs, so the first
    // run after the last send can be months gone: Roberts Oxygen's every-15-days manifest, last sent
    // 2026-07-16, came back on 2026-10-15 with a new invoice and read 'Next Process Date: Jul 31'.
    // Today counts - a run due today has not been missed yet.
    if (nextDt != null && nextDt < today) {
      nextDt = ruleObj.after(today, true);
    }
    // var nextProcessDt = new Date( nextDt.getTime() + Math.abs(nextDt.getTimezoneOffset()*60000) );
    const nextProcessDt = nextDt;
    return nextProcessDt;
  }

  async getManufacturer(manufId: number) {
    const q = this.uow.createQuery(Manufacturer, "Manufacturers").where({ id: manufId });

    const r = await q.execute();
    return _.first(r);
  }

  async getManufacturersContaining(nameSearch: string) {
    const q = this.uow
      .createQuery(Manufacturer, "Manufacturers")
      .where({ name: { contains: nameSearch } })
      .orderBy("name");

    const r = await q.execute();
    return r;
  }

  async getManufacturerProductTypeConfigById(id: number) {
    const q = this.uow
      .createQuery(ManufacturerProductTypeConfig)
      .where({ id: id })
      .expand(["manufacturerProductTypeConfigAddons", "poDetails"]);
    const r = await q.execute();
    return _.first(r);
  }

  async getAccount(accountId: number) {
    const q = this.uow.createQuery(Account, "Accounts").where({ id: accountId });

    const r = await q.execute();
    return _.first(r);
  }

  async getReissuanceAccounts() {
    const q = this.uow
      .createQuery(Account, "Accounts")
      .where({
        allowReissuanceOrders: true,
      })
      .orderBy("accountName", false);

    const r = await q.execute();
    return r;
  }

  async getJoHeadersForRra(joh: JoHeader) {
    const whereClause = {
      // This means that this JoHeader was created by Uniformax not Proximity as a result of a return.
      proximityJoHeaderId: null,
      joStatusId: JoStatusCode.OpenProcessIncomplete,
      accountId: joh.accountId,
    };

    // TODO: review if the oldCusKey is needed any longer
    if (joh.oLD_CUS_KEY != null) {
      whereClause["legacyCustomerId"] = joh.oLD_CUS_KEY;
    }
    const q = this.uow.createQuery(JoHeader, "JoHeaders").where(whereClause);
    const r = await q.execute();
    return r;
  }

  async getProductType(productTypeId: number) {
    const q = this.uow
      .createQuery(ProductType)
      .where({ id: productTypeId })
      .expand([
        "manufacturer",
        "products.productFeatureChoices.featureChoice",
        "productTypeFeatureMaps.feature.featureChoices",
        "productTypeAltProductTypes",
        "productTypeImages",
      ]);
    const r = await q.execute();
    return _.first(r);
  }

  async getProductTypeMapsForTag(productTypeTagId: number) {
    const q = this.uow.createQuery(ProductTypeTagMap).where({ productTypeTagId: productTypeTagId }).expand(["productType.manufacturer"]);
    const r = await q.execute();
    return r;
  }

  async getProductTypeTags() {
    const q = this.uow.createQuery(ProductTypeTag);

    const r = await q.execute();
    return r;
  }

  async getBoxSizes() {
    const q = this.uow.createQuery(BoxSize);
    const r = await q.execute();
    return r;
  }

  async getFeature(featureId: number) {
    let q = this.uow.createQuery(Feature).where({ id: featureId });
    const r = await q.execute();
    return _.first(r);
  }

  async getFeatures(shouldExpandFeatureChoices = false) {
    let q = this.uow.createQuery(Feature, "Features");
    if (shouldExpandFeatureChoices) {
      q = q.expand(["featureChoices.reorderFeatureChoice"]);
    }
    const r = await q.orderBy("name").execute();
    return r;
  }

  /**
   * Both navigations are expanded because the Feature Types page refuses a delete while either
   * one still points at the row. Without them loaded the collections read as empty, the page
   * allows the delete, and the server rejects it on the foreign key instead - the operator
   * would get a save error rather than the sentence that says which rows to change first.
   */
  async getFeatureKinds() {
    const q = this.uow
      .createQuery(FeatureKind)
      .expand(["features", "manufacturerFeatureKindMaps"]);
    return await q.orderBy("id").execute();
  }

  /**
   * The id of the feature that means color, resolved from its kind rather than hard-coded.
   *
   * Replaces COLOR_FEATURE_ID = 58, which was written out twice - once exported from
   * repoint-color-dialog and once again as a private const in product-builder-dialog. The id is
   * only 58 in this deployment; another distributor running a modified Uniformax has its own
   * feature list, where 58 is some other feature and the hard-coded version would point at it
   * silently rather than fail.
   *
   * Cached after the first call. Features are reference data and the answer cannot move within
   * a session - re-kinding a feature is an administrative act, not something an operator does
   * mid-task.
   *
   * Returns null when no feature is kinded Color, which is a real state for a deployment that
   * does not sell by color at all. Callers must handle it rather than assume.
   */
  private colorFeatureIdCache: number | null | undefined;

  async getColorFeatureId(): Promise<number | null> {
    if (this.colorFeatureIdCache !== undefined) return this.colorFeatureIdCache;
    const features = await this.getFeatures();
    const color = features.find(f => f.featureKindId === FeatureKindEnum.Color);
    this.colorFeatureIdCache = color?.id ?? null;
    return this.colorFeatureIdCache;
  }

  /**
   * Which feature kinds this manufacturer requires on every style, with the kind's name for the
   * message. Empty means nothing is enforced — which is every manufacturer without a feed.
   */
  async getRequiredFeatureKinds(manufacturerId: number) {
    const q = this.uow
      .createQuery(ManufacturerFeatureKindMap)
      .where({ manufacturerId: manufacturerId })
      .expand(["featureKind"])
      // Ordered so the chooser expands counts into the same slot order every time. Without it
      // the order is whatever the server returns, which can differ between two loads of the
      // same manufacturer.
      .orderBy("featureKindId");
    return await q.execute();
  }

  /**
   * The feature kinds that at least one manufacturer WITH A FEED requires on every style.
   *
   * A feature of one of these kinds must not have its kind changed. The requirement is stated
   * against the kind and never against a feature, so re-kinding the one Color feature takes
   * every feed style out of compliance at once, with nothing on screen to say why. Clearing the
   * requirement on the manufacturer is the way out, and it is a deliberate act.
   */
  async getFeatureKindsRequiredByFeedManufacturers() {
    // Filtered here rather than in the query: the table holds one row per manufacturer per
    // required kind, so it is small, and a nested-path predicate buys nothing against it.
    const q = this.uow.createQuery(ManufacturerFeatureKindMap).expand(["featureKind", "manufacturer"]);
    const all = (await q.execute()) as unknown as ManufacturerFeatureKindMap[];
    return all.filter((m) => m.manufacturer?.manufacturerFeedStatusId !== ManufacturerFeedStatusEnum.None);
  }

  async getFeatureChoice(featureChoiceId: number) {
    const q = this.uow.createQuery(FeatureChoice).where({ id: featureChoiceId }).expand(["feature"]);
    const r = await q.execute();
    return _.first(r);
  }

  async getFeatureChoices(featureId: number) {
    const q = this.uow.createQuery(FeatureChoice).expand(["feature"]).where({ featureId: featureId });
    const r = await q.execute();
    return r;
  }

  async getProductsCountWithFeatureChoice(featureChoiceId: number) {
    const q = this.uow.createQuery(null, "GetProductsCountWithFeatureChoice").withParameters({ featureChoiceId });
    const r = await q.execute();
    return _.first(r) as any as number;
  }

  async getItemsCountWithAddon(addonId: number) {
    const q = this.uow.createQuery(null, "GetItemsCountWithAddon").withParameters({ addonId });
    const r = await q.execute();
    return _.first(r) as any as number;
  }

  createProductsForFeatureChoiceQuery(featureChoiceId: number) {
    const q = this.uow
      .createQuery(Product, "Products")
      .where({
        productFeatureChoices: { any: { featureChoiceId: featureChoiceId } },
      })
      .expand(["productType.manufacturer", "productFeatureChoices.featureChoice.feature"]);
    return q;
  }

  async getUnpostedPoHeadersForManufacturer(manufacturerId: number) {
    const q = this.uow
      .createQuery(PoHeader, "PoHeaders")
      .where({
        manufacturerId: manufacturerId,
        poStatusId: PoStatusCode.NewUnposted,
      })
      .expand(["manufacturer", "poDetails"])
      .orderBy("poDate", true);
    const r = await q.execute();
    return r;
  }


  async getPoHeader(poHeaderId: number) {
    const q = this.uow
      .createQuery(PoHeader)
      .where({ id: poHeaderId })
      .expand(["manufacturer", "shippingWarehouse", "poDetails", "poDetails.joDetail", "poNotes"]);
    const r = await q.execute();
    return _.first(r);
  }

  async getPoHeaders(poHeaderIds: number[]) {
    const q = this.uow
      .createQuery(PoHeader, "PoHeaders")
      .where({ id: { in: poHeaderIds } })
      .expand(["poDetails"]);
    const r = await q.execute();
    return r;
  }

  async getPoDetail(poDetailId: number) {
    const q = this.uow.createQuery(PoDetail, "PoDetails").where({ id: poDetailId });
    const r = await q.execute();
    return r.length ? r[0] : null;
  }

  async getPoDetailForDelete(poDetailId: number) {
    const q = this.uow
      .createQuery(PoDetail, "PoDetails")
      .where({ id: poDetailId })
      .expand(["poDetailCancels", "itemDetails.itemDetailAddons", "itemDetails.itemDetailLogs"]);
    const r = await q.execute();
    return r.length ? r[0] : null;
  }

  async getPoDetails(poHeaderId: number) {
    const q = this.uow.createQuery(PoDetail, "PoDetails").where({ poHeaderId: poHeaderId }).expand([
      "product.productType",
      "product.productType.manufacturer",
      "product.productFeatureChoices.featureChoice",
      "joDetail",
      "joDetail.joHeader",
      "joDetail.joHeader.account",
      "manufacturerProductTypeConfig",
      // 'poHeader.poNotes'
    ]);
    const r = await q.execute();
    return r;
  }

  async getPoDetailsExpanded(poHeaderId: number) {
    const q = this.uow
      .createQuery(PoDetail, "PoDetails")
      .where({ poHeaderId: poHeaderId })
      .expand([
        "poNotes",
        "poDetailCancels",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "origProduct.productFeatureChoices.featureChoice",
        "joDetail.joHeader.account",
        "itemDetails",
        "manufacturerProductTypeConfig",
      ]);
    const r = await q.execute();
    return r;
  }

  async getPoDetailsForReceive(poHeaderId: number) {
    const q = this.uow
      .createQuery(PoDetail, "PoDetails")
      .where({ poHeaderId: poHeaderId })
      .expand([
        "poHeader.manufacturer",
        "poHeader.poStatus",
        "poNotes",
        "poDetailCancels",
        "product.productType.manufacturer",
        "product.productType.productTypeFeatureMaps",
        "product.productFeatureChoices.featureChoice.feature",
        "joDetail.joHeader.account",
        "joDetail.joDetailAddons.addon.addonStation",
        "itemDetails.itemDetailAddons.addon",
        "itemDetails.itemDetailAddons.addonBin.pendingAddonStation",
        "poTrx.poDetails",
        "manufacturerProductTypeConfig",
      ]);
    const r = await q.execute();
    return r;
  }

  async getPoTrxHistsForPo(poHeaderId: number) {
    const q = this.uow
      .createQuery(PoTrxHist, "GetPoTrxHistsForPo") // method on server
      .withParameters({ poHeaderId: poHeaderId });
    const r = await q.execute();
    return r;
  }

  async getOrphanPoDetails() {
    const q = this.uow
      .createQuery(PoDetail, "PoDetails")
      .where({ poHeaderId: null })
      .expand([
        "product.productType",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "joDetail",
        "joDetail.joHeader",
        "joDetail.joHeader.account",
        "poNotes",
      ]);
    const r = await q.execute();
    return r;
  }

  async getJoDetailById(joDetailId: number) {
    const q = this.uow
      .createQuery(JoDetail, "JoDetails")
      .where({ id: joDetailId })
      .expand([
        "joHeader.account",
        "itemDetails.product.productType.manufacturer",
        "joDetailAddons.addon.addonStation",
        "product.productType.manufacturer",
        "product.productType.productTypeAltProductTypes",
        "product.productFeatureChoices.featureChoice.feature",
        "cancellationRequestDetails",
        "returnRequestDetails",
      ]);
    const r = await q.execute();
    return _.first(r);
  }

  async getJoDetailsForIds(jodIds: number[]) {
    const delimJodIds = jodIds.map((id) => id.toString()).join(",");
    const q = this.uow.createQuery(JoDetail, "GetJoDetailsForIds").withParameters({ delimJodIds: delimJodIds });
    const r = await q.execute();
    return r;
  }

  async getPoDetailsForIds(podIds: number[]) {
    const delimPodIds = podIds.map((id) => id.toString()).join(",");
    const q = this.uow.createQuery(PoDetail, "GetPoDetailsForIds").withParameters({ delimPodIds: delimPodIds });
    const r = await q.execute();
    return r;
  }

  async getPoTrx(poTrxId: number) {
    const q = this.uow
      .createQuery(PoTrx, "PoTrxs")
      .where({ id: poTrxId })
      .expand([
        "poTrxHists",
        "poDetails",
        "poDetails.poNotes",
        "poDetails.poHeader",
        "poDetails.joDetail.joHeader",
        "poDetails.itemDetails",
        "poDetails.itemDetails.itemDetailAddons.addon",
      ]);
    const r = await q.execute();
    return _.first(r);
  }

  async getJoDetailsForPoModification(productTypeId: number) {
    const q = this.uow
      .createQuery(JoDetail, "GetJoDetailsForPoModification") // method on server
      .withParameters({ productTypeId: productTypeId });
    const r = await q.execute();
    return r;
  }

  async getJoDetail(joDetailId: number) {
    const q = this.uow.createQuery(JoDetail, "JoDetails").where({ id: joDetailId });
    const r = await q.execute();
    return r.length ? r[0] : null;
  }

  async getJoDetails(joHeaderId: number) {
    const q = this.uow
      .createQuery(JoDetail, "JoDetails")
      .where({ joHeaderId: joHeaderId })
      .expand([
        "product.productType",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "itemDetails",
        "accountPurchaseItemDetails",
        "joDetailAddons.addon",
        "poDetails",
        "poDetails.poDetailCancels",
        "poDetails.itemDetails",
        "poDetails.poHeader",
        "joNotes",
        "cancellationRequestDetails",
        "returnRequestDetails",
      ]);
    const r = await q.execute();
    return r;
  }

  async getJoDetailsSimple(joHeaderId: number) {
    const q = this.uow.createQuery(JoDetail, "JoDetails").where({ joHeaderId: joHeaderId });
    const r = await q.execute();
    return r;
  }

  // OR-chain rather than FilterQueryOp.In so the translated OData stays within what
  // the other joHeaderId queries here already exercise. Chunked because the whole
  // predicate rides in the query string — a few hundred ids in one request overruns
  // the max request-line length and fails at the network layer (status 0).
  async getJoDetailsForJoHeaderIds(joHeaderIds: number[]) {
    const results: JoDetail[] = [];
    for (const chunk of _.chunk(joHeaderIds, 40)) {
      const preds = chunk.map((id) => Predicate.create("joHeaderId", FilterQueryOp.Equals, id));
      const q = this.uow
        .createQuery(JoDetail, "JoDetails")
        .where(Predicate.or(preds))
        .expand(["product.productType.manufacturer", "joHeader.invoiceHeaders"]);
      const r = await q.execute();
      results.push(...r);
    }
    return results;
  }

  async getInvoiceDetailsForJoReturn(joHeaderId: number) {
    const q = this.uow
      .createQuery(InvoiceDetail, "InvoiceDetails")
      .where({
        "joDetail.joHeaderId": joHeaderId,
        "invoiceHeader.invoiceStatusId": InvoiceStatusCode.InvoicePosted,
      })
      .expand([
        "invoiceHeader",
        "joDetail.returnRequestDetails",
        "joDetail.returnRequestDetails.creditMemos",
        "joDetail.product.productType",
        "joDetail.product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "joDetail.joDetailAddons.addon",
        "joDetail.itemDetails",
        "joDetail.joHeader.account",
        "joDetail.cancellationRequestDetails",
      ]);
    const r = await q.execute();
    return r;
  }

  async getReturnRequestDetails(returnRequestId: number) {
    const q = this.uow
      .createQuery(ReturnRequestDetail)
      .where({
        returnRequestId: returnRequestId,
      })
      .expand([
        "creditMemos",
        "returnRequest.joHeader.account",
        "joDetail.invoiceDetails.invoiceHeader",
        "joDetail.product.productType.manufacturer",
        "joDetail.product.productFeatureChoices.featureChoice",
        "joDetail.joDetailAddons.addon",
        "joDetail.itemDetails",
      ]);
    const r = await q.execute();
    return r;
  }

  async getJoDetailsByJoPullBatchId(joPullBatchId: number) {
    const q1 = this.uow
      .createQuery(JoDetail, "JoDetails")
      .where({ "joHeader.joHeaderPull.joPullBatchId": joPullBatchId })
      .expand([
        "joDetailAddons.addon",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "joHeader.joHeaderPull.joPullBatch",
        "joHeader.account",
        "itemDetails.product.productType.manufacturer",
        "itemDetails.itemDetailAddons.addon",
        "itemDetails.itemDetailAddons.addonBin",
        "accountPurchaseItemDetails",
        "poDetails.poDetailCancels",
        "poDetails.itemDetails",
        "poDetails.poHeader",
        "cancellationRequestDetails",
        "returnRequestDetails",
      ]);
    const r = await q1.execute();
    return r;
  }

  async getJoDetailsForAudit(joHeaderId: number) {
    const q1 = this.uow
      .createQuery(JoDetail, "JoDetails")
      .where({ joHeaderId: joHeaderId })
      .expand([
        "product.productType",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "joHeader",
        "joHeader.account",
        "poDetails",
        "poDetails.poHeader",
        "poDetails.itemDetails",
        "poDetails.poDetailCancels",
        "itemDetails",
        "accountPurchaseItemDetails",
        "itemDetails.product.productType",
        "itemDetails.product.productType.manufacturer",
        "itemDetails.product.productFeatureChoices.featureChoice",
        "poDetails.itemDetails.product.productType",
        "poDetails.itemDetails.product.productType.manufacturer",
        "poDetails.itemDetails.product.productFeatureChoices.featureChoice",
        "cancellationRequestDetails",
        "returnRequestDetails",
      ]);
    const r = await q1.execute();
    return r;
  }

  createJoHeadersForAuditQuery() {
    const q = this.uow
      .createQuery(JoHeader)
      .expand([
        "account",
        "joDetails.poDetails.itemDetails",
        "joDetails.itemDetails",
        "joDetails.poDetails.poDetailCancels",
        "joDetails.joNotes",
        "joDetails.poDetails.poNotes",
        "joNotes",
      ]);

    return q;
  }

  async getJoHeadersForAuditCalc(takeCount: number, skipCount: number) {
    const q = this.createJoHeadersForAuditQuery()
      .where({ joStatusId: JoStatusCode.OpenProcessComplete })
      .take(takeCount)
      .skip(skipCount)
      .orderBy("id");
    const r = await q.execute();
    return r;
  }

  async getJoHeaderForAuditCalc(joHeaderId: number) {
    const q = this.createJoHeadersForAuditQuery().where({ id: joHeaderId });

    const r = await q.execute();
    return _.first(r);
  }

  // used to insure that a pull batch is not avail if any JoDetails have an invoice
  async getInvoiceDetailsByJoPullBatchId(joPullBatchId: number) {
    const q = this.uow
      .createQuery(InvoiceDetail, "InvoiceDetails")
      .where({ "joDetail.joHeader.joHeaderPull.joPullBatchId": joPullBatchId });
    const r = await q.execute();
    return r;
  }

  async getJoPullBatches() {
    const q = this.uow.createQuery(JoPullBatch, "JoPullBatches").expand("joHeaderPulls.joHeader.account");

    const r = await q.execute();
    return r;
  }

  async getJoPullBatch(joPullBatchId: number) {
    const q = this.uow.createQuery(JoPullBatch, "JoPullBatches").where({ id: joPullBatchId }).expand("joHeaderPulls");
    const r = await q.execute();
    return _.first(r);
  }

  async getJoHeaderWithInvInfoById(joHeaderId: number) {
    const q = this.uow
      .createQuery(JoDetail, "JoDetails")
      .where({ "joHeader.id": joHeaderId })
      .expand([
        "product.productType",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "invoiceDetails",
        "joHeader.account",
        "joHeader.invoiceHeaders",
        "joDetailAddons.addon",
        "cancellationRequestDetails",
        "returnRequestDetails",
      ]);
    const joDetails = await q.execute();
    if (joDetails.length === 0) {
      return null;
    }
    return joDetails[0].joHeader;
  }

  async getJoDetailsForJoHeader(joHeaderId: number) {
    const q = this.uow
      .createQuery(JoDetail, "JoDetails")
      .where({ "joHeader.id": joHeaderId })
      .expand([
        "itemDetails",
        "accountPurchaseItemDetails",
        "itemDetails.itemDetailAddons.addon",
        "product.productType",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "invoiceDetails",
        "joHeader.account",
        "joHeader.invoiceHeaders",
        "joDetailAddons.addon",
        "poDetails",
        "poDetails.poDetailCancels",
        "poDetails.itemDetails",
        "poDetails.poHeader",
        "cancellationRequestDetails",
        "returnRequestDetails",
      ]);

    const r = await q.execute();
    return r;
  }

  async getItemDetailsForUninvoicedAccountOwnedInventory() {
    const q = this.uow
      .createQuery(ItemDetail)
      .where({
        and: [{ not: { accountPurchaseJoDetailId: null } }, { accountPurchaseInvoiceHeaderId: null }],
      })
      .expand(["accountPurchaseJoDetail.joHeader", "poDetail.poHeader"]);
    const r = await q.execute();
    return r;
  }

  async getItemDetailByIdForScan(itemDetailId: string) {
    const q = this.uow.createQuery(ItemDetail, "ItemDetails").where({ id: itemDetailId }).expand(["joDetail.joHeader"]);
    const r = await q.execute();
    return _.first(r);
  }

  createScanStationQuery(backordersOnly: boolean) {
    let q = this.uow
      .createQuery(ItemDetail, "ItemDetails")
      .where({ itemDetailStatusId: ItemDetailStatusEnum.InProcessPendingShipPrep })
      .expand([
        "joDetail.joHeader.account",
        "joDetail.joHeader.shipFrequency",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice.feature",
        "itemDetailStatus",
      ]);
    if (backordersOnly) {
      q = q.where({ joDetailId: { ne: null } });
    }
    return q;
  }

  async getItemDetailByIdForScanStation(itemDetailId: string) {
    const q = this.uow
      .createQuery(ItemDetail, "ItemDetails")
      .where({ id: itemDetailId })
      .expand([
        "joDetail.joHeader.account",
        "joDetail.joHeader.shipFrequency",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice.feature",
        "itemDetailStatus",
        "shipBin",
      ]);
    const r = await q.execute();
    return _.first(r);
  }

  async getItemDetailById(itemDetailId: string) {
    const q = this.uow
      .createQuery(ItemDetail, "ItemDetails")
      .where({ id: itemDetailId })
      .expand([
        "account",
        "itemDetailAddons.addon",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice.feature",
        "product.productType",
      ]);
    const r = await q.execute();
    return _.first(r);
  }

  async getItemDetailExactMatchSummaryForJoPullBatch(joPullBatchId: number) {
    const q = this.uow.createQuery(null, "GetItemDetailExactMatchSummaryForJoPullBatch").withParameters({ joPullBatchId });

    var r = q.execute();
    var r1 = r as unknown as ItemDetailMatchSummary[];
    return r1;
  }

  async getItemDetailAllMatchSummaryForJoPullBatch(joPullBatchId: number) {
    const q = this.uow.createQuery(null, "GetItemDetailAllMatchSummaryForJoPullBatch").withParameters({ joPullBatchId });

    var r = q.execute();
    var r1 = r as unknown as ItemDetailMatchSummary[];
    return r1;
  }

  async getItemDetailsByItemDetailSummary(ids: ItemDetailSummary) {
    // The summary groups by (productId, itemDetailStatusId, accountId), so this
    // detail query MUST filter by accountId too — otherwise a click on the
    // "Metro PT" summary row returns items from every account (and Unitec
    // stock), which breaks anything downstream that assumes uniform account
    // context (e.g. account-owned barcode labels).
    // Server encodes "not account-owned" as accountId=0; translate to null-eq.
    const accountPredicate = ids.accountId
      ? Predicate.create("accountId", FilterQueryOp.Equals, ids.accountId)
      : Predicate.create("accountId", FilterQueryOp.Equals, null);
    const q = this.uow
      .createQuery(ItemDetail, "ItemDetails")
      .where(
        Predicate.create("productId", FilterQueryOp.Equals, ids.productId)
          .and("itemDetailStatusId", FilterQueryOp.Equals, ids.itemDetailStatusId)
          .and(accountPredicate)
      )
      .expand([
        "itemDetailAddons.addon",
        "invoiceHeader",
        "invoiceHeader.invoiceDetails",
        "joDetail",
        "poDetail",
        "itemBin",
        "account",
        "itemReplacement",
        "joDetail.joHeader",
        "joDetail.joHeader.account",
        "joDetail.joDetailAddons.addon",
      ]);

    const r = await q.execute();
    return r;
  }

  async getItemDetailsByJoHeaderId(joHeaderId: number) {
    const q = this.uow.createQuery(ItemDetail, "GetItemDetailsByJoHeaderId").withParameters({ joHeaderId: joHeaderId });
    const r = await q.execute();
    return r;
  }

  async getItemDetailsByJoDetailId(joDetailId: number) {
    const q = this.uow
      .createQuery(ItemDetail, "ItemDetails")
      .where({ joDetailId: joDetailId })
      .expand([
        "product.productType",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "itemDetailAddons.addon",
      ]);
    const r = await q.execute();
    return r;
  }

  createItemDetailQuery(productTypeId?: number, itemDetailStatusIds?: number | number[], itemDetailId?: string) {
    const whereClause = {};
    if (productTypeId) {
      whereClause["product.productTypeId"] = productTypeId;
    }
    const statusIds = this.normalizeStatusIds(itemDetailStatusIds);
    if (statusIds.length === 1) {
      // Use a direct equality to support status 0 (InInventory), which Breeze drops from {in:[0]} in some versions.
      whereClause["itemDetailStatusId"] = statusIds[0];
    } else if (statusIds.length > 1) {
      whereClause["itemDetailStatusId"] = { in: statusIds };
    }
    if (itemDetailId) {
      whereClause["id"] = itemDetailId;
    }
    const q = this.uow
      .createQuery(ItemDetail, "ItemDetails")
      .where(whereClause)
      .expand([
        "product.productType",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "itemDetailAddons.addon",
      ]);
    return q;
  }

  /**
   * Free-text ItemDetail search for the Item Lookup page. No product-type prefilter.
   * Matches against inventory id, product type style/description, manufacturer name,
   * and the cached feature-choices extract.
   */
  createItemDetailSearchQuery(searchText: string, itemDetailStatusIds?: number | number[]) {
    const whereClause: any = {};
    const text = (searchText ?? "").trim();
    if (text.length > 0) {
      whereClause.or = [
        { id: { contains: text } },
        { "product.productType.style": { contains: text } },
        { "product.productType.description": { contains: text } },
        { "product.productType.manufacturer.name": { contains: text } },
      ];
    }
    const statusIds = this.normalizeStatusIds(itemDetailStatusIds);
    if (statusIds.length === 1) {
      whereClause["itemDetailStatusId"] = statusIds[0];
    } else if (statusIds.length > 1) {
      whereClause["itemDetailStatusId"] = { in: statusIds };
    }
    const q = this.uow
      .createQuery(ItemDetail, "ItemDetails")
      .where(whereClause)
      .expand([
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "itemBin",
        "account",
        "itemDetailStatus",
      ]);
    return q;
  }

  private normalizeStatusIds(value: number | number[] | null | undefined): number[] {
    if (value == null) return [];
    if (Array.isArray(value)) return value.filter((v) => v != null);
    return [value];
  }

  async getAltItemDetailsForStyle(productTypeId: number) {
    const q = this.uow
      .createQuery(ItemDetail, "GetAltItemDetailsForStyle") // method on server
      .withParameters({ productTypeId: productTypeId });
    const r = await q.execute();
    return r;
  }

  async getEmptyProductTypes() {
    const q = this.uow.createQuery(ProductType, "GetEmptyProductTypes"); // method on server
    const r = await q.execute();
    return r;
  }

  async getProductTypesForManuf(manufacturerId: number) {
    const q = this.uow
      .createQuery(ProductType, "ProductTypes")
      .where({
        manufacturerId: manufacturerId,
      })
      .orderBy("style");
    const r = await q.execute();
    return r;
  }

  async getMatchingAddon(addon: Addon) {
    const q = this.uow.createQuery(Addon).where({
      accountId: addon.accountId,
      nameAndLocation: addon.nameAndLocation,
      not: { id: addon.id },
    });
    const r = await q.execute();
    return _.first(r);
  }

  /** Returns other addons (any account) sharing the same Grouping Key at the same station.
   * Items needing this addon will batch together at processing time with items needing any
   * returned addon - drives the inline collision warning in edit-addon-dialog. */
  async getAddonsByGroupingKeyAndStation(addon: Addon): Promise<Addon[]> {
    const key = (addon.groupingKey || "").trim();
    if (!key || addon.addonStationId == null) return [];
    const q = this.uow
      .createQuery(Addon)
      .where({
        groupingKey: key,
        addonStationId: addon.addonStationId,
        not: { id: addon.id },
      })
      .expand("account");
    return await q.execute();
  }

  async getMatchingEntity<T extends Entity>(type: { new (): T }, entity: T & { id: any }, altKeyFieldNames: string[]) {
    var opts = {
      not: { id: entity.id },
    };
    altKeyFieldNames.forEach((fieldName) => (opts[fieldName] = entity[fieldName]));

    const q = this.uow.createQuery(type).where(opts);
    const r = await q.execute();
    return _.first(r);
  }

  async getMatchingManufacturer(manufacturer: Manufacturer) {
    const q = this.uow.createQuery(Manufacturer).where({
      name: manufacturer.name,
      not: { id: manufacturer.id },
    });
    const r = await q.execute();
    return _.first(r);
  }

  async getMatchingManufacturerProductTypeConfig(mptConfig: ManufacturerProductTypeConfig) {
    const q = this.uow.createQuery(ManufacturerProductTypeConfig).where({
      configName: mptConfig.configName,
      not: { id: mptConfig.id },
    });
    const r = await q.execute();
    return _.first(r);
  }

  async getMatchingProductType(manufacturerId: number, style: string) {
    const q = this.uow.createQuery(ProductType, "ProductTypes").where({
      manufacturerId: manufacturerId,
      style: style,
    });
    const r = await q.execute();
    return _.first(r);
  }

  async getMatchingProduct(productTypeId: number, productFeatureChoices: ProductFeatureChoice[]) {
    const grp = productFeatureChoices.map((pfc) => {
      return {
        productFeatureChoices: {
          any: { "featureChoice.choiceValue": pfc.featureChoice.choiceValue },
        },
      };
    });
    const whereClause = {
      and: [{ productTypeId: productTypeId }, ...grp],
    };
    const q = this.uow.createQuery(Product, "Products").where(whereClause);

    const r = await q.execute();
    return _.first(r);
  }

  async isEmptyProduct(productId: number) {
    const q = this.uow
      .createQuery(null, "IsEmptyProduct") // method on server
      .withParameters({ productId: productId });
    const r = await q.execute();
    return r.length === 0;
  }

  async getAltProductTypeIdsFor(productTypeId: number) {
    const q = this.uow
      .createQuery(null, "GetAltProductTypeIdsFor") // method on server
      .withParameters({ productTypeId: productTypeId });
    const r = await q.execute();
    return r as any as number[];
  }

  async getAccountOwnedProductTypeIds(inventoryProgramName: string) {
    const q = this.uow
      .createQuery(null, "GetAccountOwnedProductTypeIds")
      .withParameters({ inventoryProgramName });
    const r = await q.execute();
    return r as any as number[];
  }

  /** Backs the Administration > Utilities > Enable Account Inventory page. */
  async getJoHeadersForAccountInventoryRepair(accountId: number) {
    const q = this.uow
      .createQuery(JoHeader, "GetJoHeadersForAccountInventoryRepair")
      .withParameters({ accountId })
      .expand(["account", "joStatus", "invoiceHeaders"]);
    const r = await q.execute();
    return r;
  }

  async getInventoryProgramNamesForAccount(accountId: number) {
    const q = this.uow
      .createQuery(null, "GetInventoryProgramNamesForAccount")
      .withParameters({ accountId });
    const r = await q.execute();
    return r as any as string[];
  }

  async getAccountOwnedPoHeaderIds(manufacturerId: number) {
    const q = this.uow
      .createQuery(null, "GetAccountOwnedPoHeaderIds")
      .withParameters({ manufacturerId });
    const r = await q.execute();
    return r as any as number[];
  }


  async getAltProductTypesForProductType(productTypeId: number) {
    const q = this.uow
      .createQuery(ProductTypeAlt, "ProductTypeAlts")
      .where({ productTypeId: productTypeId })
      .expand(["altProductType", "altProductType.manufacturer"]);
    const r = await q.execute();
    const altProductTypes = _.flatMap(r, (ita) => ita.altProductType);
    return altProductTypes;
  }

  async getProduct(productId: number) {
    const q = this.uow
      .createQuery(Product)
      .where({ id: productId })
      .expand(["productFeatureChoices.featureChoice", "productType.productTypeFeatureMaps"]);
    const r = await q.execute();
    return _.first(r);
  }

  async getProductFeatureChoice(productId: number, featureChoiceId: number) {
    const q = this.uow
      .createQuery(ProductFeatureChoice)
      .where({ productId, featureChoiceId })
      .expand(["product.productType", "featureChoice.feature"]);
    const r = await q.execute();
    return _.first(r);
  }

  async getProductsForProductType(productTypeId: number) {
    const q = this.uow
      .createQuery(Product, "Products")
      .where({ productTypeId: productTypeId })
      .expand(["productFeatureChoices.featureChoice.feature"]);
    const r = await q.execute();
    return r;
  }

  /** One manufacturer's style by its SKU (no case), or undefined. */
  async getProductTypeByStyle(manufacturerId: number, style: string) {
    const q = this.uow.createQuery(ProductType, "ProductTypes").where({ manufacturerId: manufacturerId, style: style.trim() });
    const r = await q.execute();
    return _.first(r);
  }

  async getProductTypeTagMaps(productTypeId: number) {
    const q = this.uow.createQuery(ProductTypeTagMap, "ProductTypeTagMaps").where({ productTypeId: productTypeId });
    return await q.execute();
  }

  /** Gatekeeper C2: the old-to-new product map rows of one old style. */
  /** Gatekeeper C4: confirmed map lines onto products of this style, as old product id -> new product id. */
  async getReplacementMapIntoStyle(newProductTypeId: number): Promise<ReplacementMap> {
    const q = this.uow
      .createQuery(ProductReplacement, "ProductReplacements")
      .where({ "newProduct.productTypeId": newProductTypeId, confirmedTs: { ne: null } });
    const r = await q.execute();
    return new Map(r.map((x) => [x.oldProductId, x.newProductId] as [number, number]));
  }

  async getProductReplacementsForOldStyle(oldProductTypeId: number) {
    const q = this.uow.createQuery(ProductReplacement, "ProductReplacements").where({ "oldProduct.productTypeId": oldProductTypeId });
    return await q.execute();
  }

  async getProductEOQs(productTypeId: number) {
    const q = this.uow.createQuery(ProductEOQ, "ProductEOQs").where({ "product.productTypeId": productTypeId }).expand(["product"]);
    const r = await q.execute();
    return r;
  }

  async canDeleteProductType(productTypeId: number) {}

  async getNextInvoiceHeaderId(joHeaderId: number) {
    // find the 'next' invoice id for this JoHeader;
    const q = this.uow.createQuery(InvoiceHeader, "InvoiceHeaders").where({ joHeaderId: joHeaderId });
    const r = await q.execute();
    if (r.length === 0) {
      return joHeaderId + "-01";
    }
    const invoices = _.sortBy(r, (inv) => inv.id);
    const lastId = _.last(invoices).id;
    const lastTwoDigits = lastId.substring(lastId.length - 2);
    const nextSuffix = parseInt(lastTwoDigits, 10) + 1;
    const newSuffix = ("0" + nextSuffix).slice(-2);
    return joHeaderId + "-" + newSuffix;
  }

  async getShipment(shipmentId: number) {
    const q = this.uow.createQuery(Shipment, "Shipments").where({ id: shipmentId }).expand(["invoiceHeaders.joHeader", "shippingBoxes"]);
    const r = await q.execute();
    return _.first(r);
  }

  async getManifest(manifestId: number) {
    const q = this.uow.createQuery(Manifest, "Manifests").where({ id: manifestId }).expand(["manifestGroups"]);
    const r = await q.execute();
    return _.first(r);
  }

  async getManifestGroup(manifestGroupId: number) {
    const q = this.uow
      .createQuery(ManifestGroup, "ManifestGroups")
      .where({ id: manifestGroupId })
      .expand(["manifestGroupInvoices.invoiceHeader"]);
    const r = await q.execute();
    return _.first(r);
  }

  // async getManifestsThatAreCurrentlyDue() {
  //   const today = UtilFns.startOfDay(new Date());
  //   const q = this.uow
  //     .createQuery(Manifest, 'Manifests')
  //     .where({ nextProcessDate: { le: today }});

  //   const r = await q.execute();
  //   return r;
  // }

  async getManifestInvoicesForManifestGroup(manifestGroupId: number) {
    const q = this.uow
      .createQuery(InvoiceHeader, "GetManifestInvoicesForManifestGroup")
      .withParameters({ manifestGroupId: manifestGroupId })
      .expand(this.getInvoiceHeaderExpandForManifest());
    const r = await q.execute();
    return r;
  }

  async getManifestInvoicesPostedButUnsent(manifestId: number) {
    const q = this.uow
      .createQuery(InvoiceHeader, "GetManifestInvoicesPostedButUnsent")
      .withParameters({ manifestId: manifestId })
      .expand(this.getInvoiceHeaderExpandForManifest());
    const r = await q.execute();
    return r;
  }

  private getInvoiceHeaderExpandForManifest() {
    return [
      "invoiceDetails",
      "invoiceDetails.joDetail",
      "invoiceDetails.joDetail.product.productType",
      "invoiceDetails.joDetail.product.productType.manufacturer",
      "invoiceDetails.joDetail.product.productFeatureChoices.featureChoice",
      "invoiceDetails.joDetail.joDetailAddons.addon",
      "shipment",
      "shipment.shippingBoxes",
      "joHeader.account",
      "manifestGroupInvoices.manifestGroup.manifest.manifestGroups",
    ];
  }

  async getInvoice(invoiceId: string) {
    const q = this.uow
      .createQuery(InvoiceHeader, "InvoiceHeaders")
      .where({ id: invoiceId })
      .expand([
        "invoiceDetails",
        "invoiceDetails.joDetail",
        "invoiceDetails.joDetail.poDetails",
        "invoiceDetails.joDetail.poDetails.poDetailCancels",
        "invoiceDetails.joDetail.poDetails.itemDetails",
        "invoiceDetails.joDetail.product.productType",
        "invoiceDetails.joDetail.product.productType.manufacturer",
        "invoiceDetails.joDetail.product.productFeatureChoices.featureChoice",
        "invoiceDetails.joDetail.joDetailAddons.addon",
        "shipment",
        "shipment.shippingBoxes",
        "shipment.invoiceHeaders",
        "joHeader.account",
        "joHeader.manifest",
      ]);
    const r = await q.execute();
    return _.first(r);
  }

  // Needed because some accounts can have thousands of invoices.
  createInvoicesForAccountQuery(accountId: number) {
    const q = this.uow
      .createQuery(InvoiceHeader)
      .where({ "joHeader.accountId": accountId })
      .orderByDesc("id");

    return q;
  }

  /** All CreditMemos (Memo + PrepaymentRefund + PcardRefund) for a given account,
   *  feeds the "Credit Memos and Refunds" tab on the Accounts page. The ledger
   *  entries drive the expander breakdown (Product Credit / Restocking Charge / ...). */
  createCreditMemosForAccountQuery(accountId: number) {
    const q = this.uow
      .createQuery(CreditMemo, "CreditMemos")
      .where({ "joHeader.accountId": accountId })
      .expand(["joHeader.account", "creditMemoStatus", "creditMemoType", "creditMemoLedgerEntries.kind"]);
    return q;
  }

  async getInvoicesByJobOrderForFreightCalc(joHeaderId: number) {
    const q = this.uow
      .createQuery(InvoiceHeader, "InvoiceHeaders")
      .where({ joHeaderId: joHeaderId })
      .expand(["invoiceDetails.joDetail", "shipment"]);

    return q.execute();
  }

  async getInvoicesOnSameShipment(invoiceHeaderId: string) {
    const q = this.uow.createQuery(InvoiceHeader, "InvoiceHeaders").where({ id: invoiceHeaderId }).expand([
      // All included in last expand.
      // 'shipment',
      // 'shipment.invoiceHeaders',
      // 'shipment.invoiceHeaders.joHeader',
      "shipment.invoiceHeaders.joHeader.account",
      "shipment.invoiceHeaders.joHeader.manifest",
    ]);

    const r = await q.execute();
    const invoiceHeader = _.first(r);
    return invoiceHeader?.shipment?.invoiceHeaders ?? [invoiceHeader]; // in case the invoiceHeader does not have a shipment ( converted data)
  }

  async getInvoicesReadyForEod() {
    const q = this.uow
      .createQuery(InvoiceHeader, "InvoiceHeaders")
      .where({ invoiceStatusId: InvoiceStatusCode.VoucherLabeled })
      .expand(["joHeader.account", "joHeader.manifest", "shipment.invoiceHeaders", "invoiceDetails.joDetail"]);
    return await q.execute();
  }

  /** Diagnostic snapshot for the Order Analysis admin page.
   * Accepts a Proximity Job Order Guid OR a Uniformax JoHeader.Id. */
  async getOrderAnalysis(joKey: string): Promise<OrderAnalysis> {
    const url = environment.apiRoot + "api/proximity/getOrderAnalysis?joKey=" + encodeURIComponent(joKey);
    return await firstValueFrom(this.http.get<OrderAnalysis>(url));
  }

  /** Paginated card list for the Order Analysis admin page. */
  async getOrderAnalysisCards(skip: number, take: number, accountNameContains?: string): Promise<OrderAnalysisListResult> {
    let url = `${environment.apiRoot}api/proximity/getOrderAnalysisCards?skip=${skip}&take=${take}`;
    if (accountNameContains?.trim()) {
      url += "&accountNameContains=" + encodeURIComponent(accountNameContains.trim());
    }
    return await firstValueFrom(this.http.get<OrderAnalysisListResult>(url));
  }

  /** Job Orders that are fully shipped and fully invoiced but never left Open/Process Complete.
   * Backs the Administration > Utilities > Stranded Job Orders page. Read-only. */
  async findStrandedOrders(): Promise<StrandedOrderResult> {
    const url = environment.apiRoot + "api/utilities/findStrandedOrders";
    return await firstValueFrom(this.http.get<StrandedOrderResult>(url));
  }

  /** Close the given stranded job orders. The server checks each one again and closes only the
   * ones that are complete; the rest come back in Refused with a reason. */
  async closeCompleteStrandedOrders(joHeaderIds: number[]): Promise<CloseStrandedResult> {
    const url = environment.apiRoot + "api/utilities/closeCompleteOrders";
    return await firstValueFrom(this.http.post<CloseStrandedResult>(url, joHeaderIds));
  }

  async getInvoicesPostedToday(): Promise<InvoiceHeader[]> {
    const q = this.uow
      .createQuery(InvoiceHeader, "GetInvoicesPostedToday")
      .expand(["joHeader.account", "joHeader.manifest", "shipment.invoiceHeaders", "invoiceDetails.joDetail"]);
    return await q.execute();
  }

  async getVoucherInvoices() {
    const q = this.uow
      .createQuery(InvoiceHeader, "InvoiceHeaders")
      .where({
        invoiceStatusId: {
          in: [InvoiceStatusCode.Voucher, InvoiceStatusCode.VoidError],
        },
      })
      .expand(["joHeader.account", "shipBin", "shipment", "shipment.shippingBoxes"]);
    const r = await q.execute();
    return r;
  }

  async getVoucherInvoicesOnAccount(accountId: number) {
    const q = this.uow
      .createQuery(InvoiceHeader, "InvoiceHeaders")
      .where({
        invoiceStatusId: {
          in: [InvoiceStatusCode.Voucher, InvoiceStatusCode.VoucherLabeled, InvoiceStatusCode.VoidError, InvoiceStatusCode.Printed],
        },
        "joHeader.accountId": accountId,
      })
      .expand(["joHeader", "shipment", "shipment.shippingBoxes"]);
    const r = await q.execute();
    return r;
  }

  async getReturnRequestsByJoHeaderId(joHeaderId: number) {
    const q = this.uow
      .createQuery(ReturnRequest, "ReturnRequests")
      .where({ joHeaderId: joHeaderId })
      .expand(["returnRequestDetails", "returnRequestDetails.creditMemos"]);
    const r = await q.execute();
    return r;
  }

  async getReturnRequestDetailsByJoHeaderId(joHeaderId: number) {
    const q = this.uow
      .createQuery(ReturnRequestDetail, "ReturnRequestDetails")
      .where({ "joDetail.joHeaderId": joHeaderId })
      .expand(["creditMemos", "returnRequest"]);
    const r = await q.execute();
    return r;
  }

  /** v2 returns: all RRDs currently in the given phase, expanded with what
   * the Inbox + Workspace need to render. */
  async getReturnRequestDetailsInPhase(phase: string) {
    const q = this.uow
      .createQuery(ReturnRequestDetail, "ReturnRequestDetails")
      .where({ phase: phase })
      .expand(["joDetail", "returnRequest.joHeader.account", "returnReasonType"]);
    const r = await q.execute();
    return r;
  }

  /** v2 returns: fetch a set of ItemDetails by id with everything the barcode
   * print path needs (product/manufacturer + addons). Used after RouteItems
   * returns ids so the Route dialog can immediately print labels. */
  async getItemDetailsForBarcodePrint(itemDetailIds: string[]): Promise<ItemDetail[]> {
    if (!itemDetailIds || itemDetailIds.length === 0) return [];
    const q = this.uow
      .createQuery(ItemDetail, "ItemDetails")
      .where({ id: { in: itemDetailIds } })
      .expand([
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice.feature",
        "itemDetailAddons.addon.addonStation",
        "itemDetailAddons.addonBin",
      ]);
    const r = await q.execute();
    return r as ItemDetail[];
  }

  /** v2 Recovery: fetch ItemDetails (with their addons) into the EntityManager
   * so the Recovery flow can call entityAspect.setDeleted() on them and
   * save through Breeze. */
  async getItemDetailsForRecovery(itemDetailIds: string[]): Promise<ItemDetail[]> {
    if (!itemDetailIds || itemDetailIds.length === 0) return [];
    const q = this.uow
      .createQuery(ItemDetail, "ItemDetails")
      .where({ id: { in: itemDetailIds } })
      .expand(["itemDetailAddons"]);
    const r = await q.execute();
    return r as ItemDetail[];
  }

  /** v2 Recovery: fetch CreditMemos by id into the EntityManager so the
   * Recovery flow can mark them deleted via Breeze. */
  async getCreditMemosByIds(creditMemoIds: number[]): Promise<CreditMemo[]> {
    if (!creditMemoIds || creditMemoIds.length === 0) return [];
    const q = this.uow
      .createQuery(CreditMemo, "CreditMemos")
      .where({ id: { in: creditMemoIds } });
    const r = await q.execute();
    return r as CreditMemo[];
  }

  /** v2 returns: every RRD in any active phase (excludes NULL = v1 and CLOSED = terminal).
   * Powers the unified Inbox view where the operator sees all in-flight returns in one list. */
  async getActiveReturnRequestDetails() {
    const q = this.uow
      .createQuery(ReturnRequestDetail, "ReturnRequestDetails")
      .where({ phase: { ne: null } })
      .where({ phase: { ne: "CLOSED" } })
      .expand(["joDetail", "returnRequest.joHeader.account", "returnReasonType"]);
    const r = await q.execute();
    return r;
  }

  /** For invoicing an account for all uninvoiced restock fees */
  async getReturnRequestDetailsForRestockFee(accountId: number) {
    const q = this.uow.createQuery(ReturnRequestDetail)
      .where({ "returnRequest.joHeader.accountId": accountId })
      .where({ chargeRestockFee: true })
      .where({ restockFeeInvoiced: false })
      .where({ "joDetail.unitRestockAmt": { 'gt': 0 }})
      .expand(["returnRequest.joHeader","joDetail.product.productType"])
    return q.execute();
  }

  /** For invoicing an account for specific uninvoiced restock fees 
   * @param rrdids - comma-delimited string of ReturnRequestDetail ids
  */
  async getReturnRequestDetailsForRestockInvoice(rrdids: string) {
    const arr = rrdids.split(',');
    const q = this.uow.createQuery(ReturnRequestDetail)
      .where({ id: { in: arr }})
      .where({ chargeRestockFee: true })
      .where({ restockFeeInvoiced: false })
      .where({ "joDetail.unitRestockAmt": { 'gt': 0 }})
      .expand(["returnRequest.joHeader","joDetail.product.productType"])
    return q.execute();
  }

  async getInvoiceHeadersByJoHeaderId(joHeaderId: number) {
    const q = this.uow
      .createQuery(InvoiceHeader, "InvoiceHeaders")
      .where({ joHeaderId: joHeaderId })
      .expand([
        "invoiceDetails",
        "invoiceDetails.joDetail",
        "shipment",
        "shipment.invoiceHeaders",
        "joHeader",
      ]);
    const r = await q.execute();
    return r;
  }

  async getInvoiceDetailsByJoHeaderId(joHeaderId: number) {
    const q = this.uow.createQuery(InvoiceDetail, "InvoiceDetails").where({ "joDetail.joHeaderId": joHeaderId }).expand(["invoiceHeader"]);
    const r = await q.execute();
    return r;
  }

  async getJoNotesForHeader(joHeaderId: number) {
    const q = this.uow.createQuery(JoNote, "JoNotes").where({ joHeaderId: joHeaderId });
    const r = await q.execute();
    return r;
  }

  async getJoNotesForDetail(joDetailId: number) {
    const q = this.uow.createQuery(JoNote, "JoNotes").where({ joDetailId: joDetailId });
    const r = await q.execute();
    return r;
  }

  async getPoNotesForHeader(poHeaderId: number) {
    const q = this.uow.createQuery(PoNote, "PoNotes").where({ poHeaderId: poHeaderId });
    const r = await q.execute();
    return r;
  }

  async getPoNotesForDetail(poDetailId: number) {
    const q = this.uow.createQuery(PoNote, "PoNotes").where({ poDetailId: poDetailId });

    const r = await q.execute();
    return r;
  }

  async getNextItemDetailIds(count: number) {
    const nextId = await this.getNextGenId("ItemDetail", count);

    const nextIds: string[] = [];
    for (let i = 0; i <= count; i++) {
      nextIds.push((nextId + i).toString());
    }
    return nextIds;
  }

  async getEntityIssues(entityTypeName: string = null, entityId: string = null) {
    let q = this.uow.createQuery(EntityIssue, "EntityIssues").expand(["account"]);
    if (entityTypeName != null) {
      q = q.where({ entityTypeName: entityTypeName, entityId: entityId });
    }
    const r = await q.execute();
    return r;
  }

  async getEntityIssuesCount(entityTypeName: string = null, entityId: string = null) {
    let q = this.uow.createQuery(EntityIssue, "EntityIssues");
    if (entityTypeName != null) {
      q = q.where({ entityTypeName: entityTypeName, entityId: entityId });
    }
    return q.executeCount();
  }

  createAddonQuery() {
    const q = this.uow.createQuery(Addon).expand(["account", "addonStation", "addonDocMaps.addonDoc", "addonImages"]);
    return q;
  }

  async getAddon(addonId: number) {
    const q = this.uow.createQuery(Addon).where({ id: addonId });
    const r = await q.execute();
    return _.first(r);
  }

  async getAddonExt(addonId: number) {
    const q = this.uow
      .createQuery(Addon)
      .where({ id: addonId })
      .expand(["addonDocMaps", "addonImages", "itemDetailAddons", "joDetailAddons", "manufacturerProductTypeConfigAddons"]);
    const r = await q.execute();
    return _.first(r);
  }

  async getAddons(accountId: number) {
    const q = this.uow.createQuery(Addon).where({ accountId: accountId }).expand(["addonStation", "addonDocMaps.addonDoc", "addonImages"]);
    const r = await q.execute();
    return r;
  }

  async getAddonStations() {
    const q = this.uow.createQuery(AddonStation);
    const r = await q.execute();
    return r;
  }

  async getAddOnStation(addOnStationId: number) {
    const q = this.uow.createQuery(AddonStation).where({ id: addOnStationId }).expand(["addons"]);
    const r = await q.execute();
    return _.first(r);
  }

  async getJodAddonsWithDocs(joDetailAddonIds: number[]) {
    let q = this.uow.createQuery(JoDetailAddon, "JoDetailAddons");
    q = q.where({ id: { in: joDetailAddonIds } }).expand(["addon.addonDocMaps.addonDoc"]);
    const r = await q.execute();
    return r;
  }

  // Note: jod must have 'product', 'joDetailAddons' and JoHeader already populated.
  async findMatchingManufacturerProductTypeConfig(jod: JoDetail) {
    const q = this.uow
      .createQuery(ManufacturerProductTypeConfig)
      .where({
        productTypeId: jod.product.productTypeId,
        isForStockOrdersOnly: false,
      })
      .expand(["manufacturerProductTypeConfigAddons"]);
    let mptcCandidates = await q.execute();
    if (mptcCandidates.length == 0) {
      return null;
    }
    // mptc must be a subset ( not a superset) of
    const jodAddons = jod.joDetailAddons;
    const jodAddonIds = jod.joDetailAddons.map((x) => x.addonId);
    mptcCandidates = mptcCandidates.filter((x) => {
      if (x.manufacturerProductTypeConfigAddons.length > jodAddons.length) {
        return false;
      }
      let isMatch = x.manufacturerProductTypeConfigAddons.every((mptcAddon) => {
        const foundItem = jodAddons.find(
          (joda) => joda.addonId == mptcAddon.addonId && (joda.additionalInfo ?? "") == (mptcAddon.additionalInfo ?? "")
        );
        return foundItem != null;
      });
      return isMatch;
    });
    if (mptcCandidates.length == 0) {
      return null;
    }
    if (mptcCandidates.length == 1) {
      return mptcCandidates[0];
    }
    // return the candidate with the longest 'match'.
    return _.maxBy(mptcCandidates, (x) => x.manufacturerProductTypeConfigAddons.length);
  }

  async findJoNoteForIssue(issue: EntityIssue) {
    let whereClause: object;
    if (issue.entityTypeName === "JoHeader") {
      whereClause = { joHeaderId: +issue.entityId };
    } else if (issue.entityTypeName === "JoDetail") {
      whereClause = { joDetailId: +issue.entityId };
    } else {
      return;
    }
    // whereClause['isIssue'] = true;
    whereClause["note"] = issue.description;
    whereClause["crtnUserInit"] = issue.crtnUserInit;
    const q = this.uow.createQuery(JoNote, "JoNotes").where(whereClause);
    const r = await q.execute();
    return r.length > 0 ? r[0] : null;
  }

  async validateCryptoGuid(tableName: string, guid: string) {
    const q = this.uow
      .createQuery(null, "ValidateCryptoGuid") // method on server
      .withParameters({ tableName: tableName, guid: guid });
    const r = await q.execute();
    return r;
  }

  async getNextGenId(keyName: string, count: number) {
    const q = this.uow
      .createQuery(null, "GetNextGenId") // method on server
      .withParameters({ keyName: keyName, count: count });
    const r = await q.execute();
    return _.first(r) as any as number;
  }

  async getProxSubmissionsPendingCount() {
    const q = this.uow.createQuery(null, "GetProxSubmissionsPendingCount"); // method on server
    const r = await q.execute();
    return _.first(r) as unknown as number;
  }

  createProxSubmissionQuery(afterDate: Date, shouldShowErrorsOnly: boolean, shouldShowPendingOnly: boolean, isCombination: boolean) {
    let q = this.uow.createQuery(ProxSubmission).where({
      isCombination: isCombination,
      crtnTs: { ge: afterDate },
    });
    if (shouldShowErrorsOnly) {
      q = q.where({ error: { ne: null } });
    }
    if (shouldShowPendingOnly) {
      q = q.where({ submissionTs: null });
    }
    return q;
  }

  async getImages(...imageIds: number[]) {
    const q = this.uow.createQuery(Image).where({ id: { in: imageIds } });
    return q.execute();
  }

  async getSystemImages(skip?: number, take?: number) {
    let q = this.uow.createQuery(SystemImage);
    if (skip) {
      q = q.skip(skip);
    }
    if (take) {
      q = q.take(take);
    }
    return q.execute();
  }

  //  AddonBins
  async getAddonBin(addonBinId: number) {
    const q = this.uow
      .createQuery(AddonBin)
      .where({
        id: addonBinId,
      })
      .expand(["account", "itemDetailAddons", "pendingAddonStation", "currentAddonStation", "currentAddonBinStationStatus"]);
    const r = await q.execute();
    return _.first(r);
  }

  async getAddonBinsByIds(addonBinIds: number[]) {
    var q = this.uow
      .createQuery(AddonBin)
      .expand(["account", "itemDetailAddons", "pendingAddonStation", "currentAddonStation"])
      .where({ id: { in: addonBinIds } });
    const r = await q.execute();
    return r;
  }

  async getAddonBins(shouldShowClosedBins: boolean) {
    var q = this.uow.createQuery(AddonBin).expand(["account", "itemDetailAddons", "pendingAddonStation", "currentAddonStation"]);
    if (!shouldShowClosedBins) {
      q = q.where({
        not: {
          addonBinStatusId: AddonBinStatusEnum.Closed,
        },
      });
    }
    return q.execute();
  }

  async getItemDetailAddonsForAddonBin(addonBinId: number) {
    const q = this.uow
      .createQuery(ItemDetailAddon)
      .where({
        addonBinId: addonBinId,
      })
      .expand([
        "addonBin",
        "itemDetail.joDetail.joHeader",
        "itemDetail.product.productType.manufacturer",
        "itemDetail.product.productFeatureChoices.featureChoice.feature",
        "addon.addonStation",
        "addon.addonDocMaps.addonDoc",
        "addon.addonImages.image",
      ]);
    const r = await q.execute();
    return r;
  }

  async getItemDetailsForAddonBin(addonBinId: number) {
    const itdas = await this.getItemDetailAddonsForAddonBin(addonBinId);
    const itemDetailSet = new Set(itdas.map((x) => x.itemDetail));
    const itemDetails = Array.from(itemDetailSet);
    return itemDetails;
  }

  async getAvailableAddonBinsFor(accountId: number, aggGroupingKey: string) {
    const q = this.uow
      .createQuery(AddonBin)
      .where({
        accountId: accountId,
        aggregateGroupingKey: aggGroupingKey,
        addonBinStatusId: {
          in: [AddonBinStatusEnum.Open, AddonBinStatusEnum.New],
        },
        currentAddonStationId: null,
      })
      .expand(["itemDetailAddons"]);
    var addonBins = await q.execute();

    return addonBins;
  }

  //  ShipBins
  async getOpenShipBins() {
    const q = this.uow
      .createQuery(ShipBin)
      .where({ isOpen: true })
      .expand(["invoiceHeaders.joHeader.account", "itemDetails.joDetail.joHeader.account"]);
    return await q.execute();
  }

  async getShipBinItemDetails(shipBinId: number) {
    const q = this.uow
      .createQuery(ItemDetail, "ItemDetails")
      .where({ shipBinId: shipBinId })
      .expand([
        "joDetail.joHeader.account",
        "joDetail.joHeader.shipFrequency",
        "product.productType.manufacturer",
        "itemDetailStatus",
      ]);
    return await q.execute();
  }

  async getShipBinByKey(aggregateGroupingKey: string) {
    const today = DateFns.startOfDay(new Date());
    const q = this.uow.createQuery(ShipBin).where({
      aggregateGroupingKey: aggregateGroupingKey,
      isOpen: true,
      or: [{ expectedShipDate: { ge: today } }, { shipFrequencyId: 0 }],
    });

    const r = await q.execute();
    return _.first(r);
  }

  /** Get all In-Process Pending Addon and In-Process Addon items across all JOs in a ship bin. */
  async getAddonItemDetailsForShipBin(shipBinId: number) {
    // First get all JO header IDs in this bin
    const binItems = await this.getShipBinItemDetails(shipBinId);
    const joHeaderIds = _.uniq(binItems.map((itd) => itd.joDetail?.joHeaderId).filter(Boolean));
    if (joHeaderIds.length === 0) return [];

    const q = this.uow
      .createQuery(ItemDetail, "ItemDetails")
      .where({
        "joDetail.joHeaderId": { in: joHeaderIds },
        itemDetailStatusId: {
          in: [
            ItemDetailStatusEnum.InProcessPendingAddon,
            ItemDetailStatusEnum.InProcessAddon,
          ],
        },
      })
      .expand([
        "joDetail.joHeader.account",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "itemDetailStatus",
        "itemDetailAddons.addonBin.currentAddonStation",
        "itemDetailAddons.addonBin.pendingAddonStation",
      ]);
    return await q.execute();
  }

  /** Get in-process ItemDetails for a JO that are NOT in Ship Prep status (i.e. still being processed). */
  async getInProcessItemDetailsByJoHeaderId(joHeaderId: number) {
    const q = this.uow
      .createQuery(ItemDetail, "ItemDetails")
      .where({
        "joDetail.joHeaderId": joHeaderId,
        itemDetailStatusId: {
          in: [
            ItemDetailStatusEnum.InProcessPendingShipPrep,
            ItemDetailStatusEnum.InProcessPendingAddon,
            ItemDetailStatusEnum.InProcessAddon,
            ItemDetailStatusEnum.InProcessShipHeld,
          ],
        },
      })
      .expand([
        "joDetail",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "itemDetailStatus",
      ]);
    return await q.execute();
  }

  /** Get open PoDetails (backorders) for a JO. */
  async getBackorderPoDetailsByJoHeaderId(joHeaderId: number) {
    const q = this.uow
      .createQuery(PoDetail, "PoDetails")
      .where({
        "joDetail.joHeaderId": joHeaderId,
      })
      .expand([
        "joDetail",
        "poHeader.manufacturer",
        "poHeader.poStatus",
        "product.productType.manufacturer",
        "product.productFeatureChoices.featureChoice",
        "poDetailCancels",
        "itemDetails",
      ]);
    const results = await q.execute();
    return results.filter((pod) => pod.getRemainingQty() > 0);
  }

  // Notification Templates

  /** gets all of the notificationTemplates for a given account along with all of the
   * 'global templates */
  async getNotificationTemplates(accountId: string) {
    let q = this.uow.createQuery(NotificationTemplate);
    if (accountId) {
      q = q.where({ or: [{ accountId: accountId }, { accountId: null }] });
    }
    return q.execute();
  }

  async getNamedNotificationTemplates(accountId: string) {
    let q = this.uow.createQuery(NotificationTemplate).where({ templateName: { ne: null } });
    if (accountId) {
      q = q.where({ or: [{ accountId: accountId }, { accountId: null }] });
    }
    return q.execute();
  }

  // Migration tables - removed

  // async getMigrationFeatureChoiceChanges(fromOrigFeatureName: string) {
  //   const q = this.uow
  //     .createQuery(_MigrationFeatureChoiceChange)
  //     .where({ fromOrigFeatureName: fromOrigFeatureName })
  //   const r = await q.execute();
  //   return r;
  // }

  // async getMigrationProductTypeChange(productTypeId: number) {
  //   const q = this.uow
  //     .createQuery(_MigrationProductTypeChange)
  //     .where({ productTypeId: productTypeId });
  //   const r = await q.execute();
  //   return _.first(r);

  // }

  async getFeatureChoiceSetForProductType(productTypeId: number) {
    let q = this.uow
      .createQuery(Product)
      .where({
        productTypeId: productTypeId,
      })
      .expand("productFeatureChoices");
    const prods = await q.execute();
    const fcIds = _.flatMap(prods, (p) => p.productFeatureChoices.map((x) => x.featureChoiceId));
    const fcIdSet = new Set(fcIds);
    return fcIdSet;
  }

  // async getMigrationFeatureChoiceSetForFeatureChange(fromFeature: Feature, toFeature: Feature) {
  //   const q = this.uow
  //     .createQuery(_MigrationFeatureChoiceChange)
  //     .where({
  //       fromOrigFeatureName: fromFeature._origName,
  //       toOrigFeatureName: toFeature._origName
  //     });
  //   const mfccs = await q.execute();

  //   const fromFcs = fromFeature.featureChoices;
  //   const fcIds = mfccs.map(mfcc => {
  //     const fc = fromFcs.find((fc => fc._origChoiceValue == mfcc.fromOrigFeatureChoiceName));
  //     return fc?.id;
  //   });
  //   return new Set(fcIds);
  // }

  // Migrations code removed.
  // async getProductTypeIdsWithMigrationsForFeatureChoice(featureChoiceId: number) {
  //   const q = this.uow.createQuery(null, 'GetProductTypeIdsWithMigrationsForFeatureChoice')
  //     .withParameters({ featureChoiceId }); // method on server
  //   const r = await q.execute();
  //   var z = r.map(x => x as any as number);
  //   return z;
  // }

  async getEntityDescription(tableName: string, entityId1: string, entityId2?: string) {
    const na = "no longer available";
    if (tableName == "Feature") {
      const f = await this.getFeature(+entityId1);
      return f ? f.name : na;
    } else if (tableName == "ProductType") {
      const pt = await this.getProductType(+entityId1);
      return pt ? pt.name + " - " + pt.description : na;
    } else if (tableName == "Manufacturer") {
      const mf = await this.getManufacturer(+entityId1);
      return mf.name;
    } else if (tableName == "FeatureChoice") {
      const fc = await this.getFeatureChoice(+entityId1);
      return fc ? fc.choiceValue + " - for feature: " + fc.feature.name : na;
    } else if (tableName == "Addon") {
      const ao = await this.getAddon(+entityId1);
      return ao ? ao.nameAndLocation : na;
    } else if (tableName == "Product") {
      const prod = await this.getProduct(+entityId1);
      return prod ? "Style: " + prod.productType.style + "  [" + prod.featureChoicesExtract + "]" : na;
    } else if (tableName == "ProductFeatureChoice") {
      // const pfc = await this.getProductFeatureChoice(+entityId1, +entityId2);
      const pDescr = await this.getEntityDescription("Product", entityId1);
      const fcDescr = await this.getEntityDescription("FeatureChoice", entityId2);
      return (pDescr ?? na) + "  FeatureChoice: " + (fcDescr ?? na);
    } else {
      return "not implemented";
    }
  }

  /** Test whether Quickbooks token is and connected company is fully set up.  Returns null if ok, error message otherwise */
  async checkQuickbooksSetup() {
    try {
      const url = environment.apiRoot + "api/quickbooks/CheckCompanySetup";
      const response = await firstValueFrom(this.http.get<{ Value: string }>(url));
      if (response.Value) {
        return "QuickBooks is not ready or the token has expired.\n\nPlease go to <a href=\"/administration/manage/qb-config\">QuickBooks Account Setup</a> to configure QuickBooks.\n\n" +
        response.Value;
      }
      return null;
    } catch (err) {
      return "Unable to reach accounting system.\n\nPlease go to <a href=\"/administration/manage/qb-config\">QuickBooks Account Setup</a> to configure QuickBooks.";
    }
  }

  async getProximityApiVersion(): Promise<string> {
    try {
      const url = environment.apiRoot + "breeze/Uniformax/GetProximityApiVersion";
      const response = await firstValueFrom(this.http.get<string>(url, { responseType: 'text' as 'json' }));
      return response;
    } catch (err: any) {
      return "Unable to get Proximity API version: " + (err.error || err.message || err) + 
      "\n\nPlease go to <a href=\"/proximity-api\">Proximity API page</a> to update the Proximity token.";
    }

  }

}
